import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IOrganisation } from '@bciti/types';
import Ajv from 'ajv';
import { Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { State } from 'src/app/store';
import { IFilters, IFiltersLastDaysErrors, ILog, IModule } from 'src/app/interfaces/logcenter/logcenter.interface';
import { Store } from '@ngrx/store';
import { environmentSelector, organisationsSelector } from 'src/app/store/global/global.selectors';
import { BcitiEnvironment } from 'src/app/interfaces/store/store.interface';
import { OrganisationServiceMock } from 'src/app/services/organisation/organisation.service.mock';
import { DatePipe } from '@angular/common';
import { OrganisationService } from 'src/app/services/organisation/organisation.service';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';

enum BUNDLES {
    CARD_CREATE = 'Card create',
    OTHER_BUNDLE = 'Other bundle',
}

@Component({
    selector: 'app-logger',
    templateUrl: './logger.component.html',
    styleUrls: ['./logger.component.scss'],
})
export class LoggerComponent implements OnInit {
    destroy$ = new Subject();
    ajv;

    public logsForm: FormGroup;
    public dateRange: FormGroup;

    constructor(private configService: ConfigurationService, private organisationService: OrganisationService, private store: Store<State>) {
        this.ajv = new Ajv({ allErrors: true });
    }

    env: Partial<BcitiEnvironment>;
    modules: IModule[];
    types: string[];
    myDatePickerFrom: any;
    myDatePickerTo: any;
    logs: ILog[];
    logsLastDays: ILog[];
    organisations: Partial<IOrganisation>[];
    bundles: string[];

    loadingLoad: boolean = false;
    loadingLastDays: boolean = false;
    isShownLoad: boolean = false;
    isShownLastDays: boolean = false;
    isButtonVisible: boolean = true;

    maxDate = new Date();

    @ViewChild('modulesSelect') modulesSelect;
    @ViewChild('moduleSelectLabel') moduleSelectLabel;
    @ViewChild('typesSelect') typesSelect;
    @ViewChild('organisationsSelect') organisationsSelect: any;
    @ViewChild('bundlesSelect') bundlesSelect: any;
    @ViewChildren('logErrors') logErrors: QueryList<ElementRef>;

    getOrganisations(): void {
        this.store
            .select(organisationsSelector)
            .pipe(
                takeUntil(this.destroy$),
                map((organisations) => {
                    this.organisations = organisations.data;
                }),
            )
            .subscribe();
    }

    getModules(): void {
        this.configService.getModules().subscribe((modules) => (this.modules = modules));
    }

    getTypes(): void {
        this.configService.getTypes().subscribe((types) => (this.types = types));
    }

    getEnv(): void {
        this.store
            .select(environmentSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((env) => {
                    this.env = env;
                }),
            )
            .subscribe();
    }

    ngOnInit(): void {
        this.dateRange = new FormGroup({
            dateStart: new FormControl(),
            dateEnd: new FormControl(),
        });

        this.logsForm = new FormGroup(
            {
                module: new FormControl(null, Validators.required),
                type: new FormControl(),
                dateRange: this.dateRange,
                // bundles: new FormControl(),
                organisation: new FormControl(),
            },
            {
                updateOn: 'change',
            },
        );

        this.getEnv();
        this.getOrganisations();
        this.getModules();
        this.getTypes();
    }

    getLogs() {
        if (this.logsForm.valid) {
            this.loadingLoad = true;
            this.isShownLastDays = false;

            const filters: IFilters = {
                env: this.env.label.toLowerCase(),
                modules: this.module.value,
            };
            if (this.type.value && this.type.value !== '') {
                filters.types = this.type.value;
            }

            if (this.dateStart.value && this.dateEnd.value) {
                filters.dateStart = new DatePipe('en').transform(this.dateStart.value, 'YYYY-MM-dd');
                filters.dateEnd = new DatePipe('en').transform(this.dateEnd.value, 'YYYY-MM-dd');
            }
            if (this.organisation.value && this.organisation.value !== '') {
                filters.organisation = this.organisation.value;
            }
            const queryString = Object.keys(filters)
                .map((key) => key + '=' + filters[key])
                .join('&');
            this.configService.getLogs(queryString).subscribe((logs) => {
                this.loadingLoad = false;
                this.isShownLoad = true;
                this.logs = logs;
            });
        }
    }

    getLastDaysModulesErrors() {
        this.loadingLastDays = true;
        this.isShownLoad = false;

        const filters: IFiltersLastDaysErrors = {
            env: this.env.label.toLowerCase(),
        };
        const queryString = Object.keys(filters)
            .map((key) => key + '=' + filters[key])
            .join('&');
        this.configService.getLastDaysModulesErrors(queryString).subscribe((logsLastDays) => {
            this.loadingLastDays = false;
            this.isShownLastDays = true;
            this.logsLastDays = logsLastDays;
        });
    }

    getErrors(module, date) {
        this.logErrors.forEach((element) => {
            element.nativeElement.hidden = true;
        });
        const logError = this.logErrors.find((le) => le.nativeElement.id === `${module}-${date}`);
        logError.nativeElement.hidden = false;
    }

    get module() {
        return this.logsForm.get('module');
    }
    get type() {
        return this.logsForm.get('type');
    }
    get dateStart() {
        return this.dateRange.get('dateStart');
    }
    get dateEnd() {
        return this.dateRange.get('dateEnd');
    }
    get organisation() {
        return this.logsForm.get('organisation');
    }
}
